/*
 * Copyright (c) 2025 MTa Learning. All Rights Reserved.
 *
 * MTa Learning proprietary information: the enclosed materials contain proprietary information of MTa Learning and
 * shall not be disclosed in whole or in any part to any third party or used by any person for any purpose, without
 * written consent of MTa Learning. Duplication of any portion of these materials shall include this notice.
 */
'use client';!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:mta-learning":true});}catch(e){}}();

import { Title as DialogTitle } from '@radix-ui/react-dialog'
import { ChartBarIcon, ClockIcon, UsersIcon } from 'lucide-react'
import React, { useEffect, useRef, useState } from 'react'

import styles from '@/components/quality-explorer/ActivityCard.module.css'
import { Activity } from '@/components/quality-explorer/types'
import { GenericDialog } from '@/components/ui/GenericDialog'
import { cn } from '@/utils/css'

/** The colour configuration for the activity card. */
const COLOUR_CONFIG = {
  Red: {
    text: 'text-kits-team',
    bar: 'bg-kits-team',
    hex: '#d51818',
  },
  Green: {
    text: 'text-kits-insights',
    bar: 'bg-kits-insights',
    hex: '#1b9147',
  },
  Blue: {
    text: 'text-kits-mini',
    bar: 'bg-kits-mini',
    hex: '#2f93cd',
  },
} as const

/** A description of the available properties for this component. */
type ActivityCardContentProps = {
  /** The activity to display on the card. */
  activity: Activity

  /** Whether this card is a focal card. Only focal cards offer the read-more affordance. */
  isActive: boolean
}

/** Process the descriptions to add the new CSS classes to any `ul` tag. */
const processDescriptions = (text?: string): string => {
  return (
    text
      ?.replace(/<ul(.*?)class="(.*?)"(.*?)>/g, `<ul$1class="$2 ${styles.list}"$3>`)
      .replace(/<ul(?![^>]*\bclass=)/g, `<ul class="${styles.list}"`) ?? ''
  )
}

/**
 * Shorten a time string like "20 - 25 Minutes plus Review" to "20–25 min" for the card's one-line meta strip. The
 * full string is always available in the read-more dialog, so this only needs to be a best effort — anything it
 * can't improve passes through unchanged.
 */
const shortenTime = (time: string): string => {
  const shortened = time
    .replace(/\s*plus\s+review\s*$/i, '')
    .replace(/\s*[-–]\s*/g, '–')
    .replace(/\bminutes?\b/i, 'min')
    .replace(/\bhours?\b/i, 'hrs')
    .replace(/\b1 hrs\b/, '1 hr')
    .trim()
  return shortened || time
}

/**
 * Shorten a participants string like "8 - 24 working in two or four teams of 4 - 6" to just its leading count
 * ("8–24") for the card's one-line meta strip. Falls back to the full string when no leading count is found.
 */
const shortenParticipants = (participants: string): string => {
  const match = participants.match(/^\d+(?:\s*(?:-|–|or|to)\s*\d+)?/i)
  return match ? match[0].replace(/\s*[-–]\s*/g, '–').replace(/\s*\bor\b\s*/i, ' or ') : participants
}

/** Map a level string to how many of the three level dots are filled. Mixed levels count as their higher level. */
const levelDots = (level: string): number => {
  if (/advanced/i.test(level)) return 3
  if (/intermediate/i.test(level)) return 2
  return 1
}

/**
 * The full, unshortened meta rows (time, participants, level) shown in the read-more dialog, styled in the card's
 * design language: hairline rules, kit-coloured icons, no repeated labels (kept for screen readers only).
 */
const ActivityMetaList: React.FC<{ activity: Activity; iconColour: string }> = ({ activity, iconColour }) => (
  <ul className={'border-t border-b border-graphite-100 py-3.5 mb-4 flex flex-col gap-2.5'}>
    <li className={'flex items-start gap-3 text-[15px] leading-snug text-graphite-500'}>
      <ClockIcon className={'w-[18px] h-[18px] mt-0.5 shrink-0'} style={{ color: iconColour }} />
      <span className={'sr-only'}>Time: </span>
      {activity.time}
    </li>
    <li className={'flex items-start gap-3 text-[15px] leading-snug text-graphite-500'}>
      <UsersIcon className={'w-[18px] h-[18px] mt-0.5 shrink-0'} style={{ color: iconColour }} />
      <span className={'sr-only'}>Participants: </span>
      {activity.participants}
    </li>
    <li className={'flex items-start gap-3 text-[15px] leading-snug text-graphite-500'}>
      <ChartBarIcon className={'w-[18px] h-[18px] mt-0.5 shrink-0'} style={{ color: iconColour }} />
      <span className={'sr-only'}>Level: </span>
      {activity.level}
    </li>
  </ul>
)

/**
 * The white card body shared by the carousel's 3D fan and its narrow-viewport snap list: a kit-coloured top bar, the
 * kit name as an eyebrow over the activity name, a one-line meta strip, and a fixed-height description so cards
 * never resize (and the page never shifts). When the description overflows, a fade hints at the rest and a read-more
 * button opens the full activity — including the unshortened meta — in a dialog.
 */
const ActivityCardContent: React.FC<ActivityCardContentProps> = ({ activity, isActive }) => {
  const [overflowing, setOverflowing] = useState(false)
  const [dialogOpen, setDialogOpen] = useState(false)
  const descriptionRef = useRef<HTMLDivElement>(null)

  // Only offer the read-more button when the fixed block actually hides content. The observer keeps this correct
  // across viewport resizes, late font loads and the card only becoming visible after mount.
  useEffect(() => {
    const element = descriptionRef.current
    if (!element) return
    const measure = () => setOverflowing(element.scrollHeight > element.clientHeight + 1)
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(element)
    return () => observer.disconnect()
  }, [])

  const colourConfig = COLOUR_CONFIG[activity.kolor]
  const descriptionHtml = { __html: processDescriptions(activity.descriptions) }
  const filledDots = levelDots(activity.level)

  return (
    <div className={'flex flex-col bg-white rounded-2xl overflow-hidden shadow-mta h-full'}>
      <div className={cn('h-[5px] shrink-0', colourConfig.bar)} />
      <div className={'flex flex-col grow p-4 md:p-6'}>
        <span className={cn('text-[11px] font-semibold uppercase tracking-widest mb-1.5', colourConfig.text)}>
          {activity.kit_name}
        </span>
        <h3 className={'text-2xl font-semibold text-graphite-500 mb-2.5'}>{activity.name}</h3>

        <div className={'flex flex-wrap items-center gap-2 mb-4 text-[13px] text-gray-600'}>
          <ClockIcon className={'w-3.5 h-3.5'} style={{ color: colourConfig.hex }} />
          <span>{shortenTime(activity.time)}</span>
          <span className={'text-graphite-200'}>&middot;</span>
          <UsersIcon className={'w-3.5 h-3.5'} style={{ color: colourConfig.hex }} />
          <span>{shortenParticipants(activity.participants)}</span>
          <span className={'text-graphite-200'}>&middot;</span>
          {/* The dots and the level label wrap as one unit, so a narrow card can't orphan the label. */}
          <span className={'flex items-center gap-1.5'}>
            <span className={'flex items-center gap-[3px]'} aria-hidden={true}>
              {[1, 2, 3].map((dot) => (
                <span
                  key={dot}
                  className={cn(
                    'w-[7px] h-[7px] rounded-full',
                    dot > filledDots && 'border-[1.5px] border-graphite-200',
                  )}
                  style={dot <= filledDots ? { background: colourConfig.hex } : undefined}
                />
              ))}
            </span>
            <span>{activity.level}</span>
          </span>
        </div>

        <div className={'border-t border-graphite-100 mb-4'} />

        <div className={'relative h-52 overflow-hidden'}>
          <div
            ref={descriptionRef}
            style={{ '--list-color': colourConfig.hex } as React.CSSProperties}
            className={'text-gray-800 h-full'}
            dangerouslySetInnerHTML={descriptionHtml}
          />
          {overflowing && (
            <div className={'absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-white to-transparent'} />
          )}
        </div>

        {isActive && overflowing && (
          <button
            type={'button'}
            onClick={(event) => {
              event.stopPropagation()
              setDialogOpen(true)
            }}
            className={cn(
              'mt-auto pt-2 self-start flex items-center gap-1.5 text-sm font-semibold underline-offset-2 hover:underline',
              colourConfig.text,
            )}
          >
            Read more
            <svg
              className={'w-3.5 h-3.5'}
              viewBox={'0 0 24 24'}
              fill={'none'}
              stroke={'currentColor'}
              strokeWidth={2.5}
              strokeLinecap={'round'}
              strokeLinejoin={'round'}
              aria-hidden={true}
            >
              <path d={'M5 12h14'} />
              <path d={'m12 5 7 7-7 7'} />
            </svg>
          </button>
        )}

        <GenericDialog
          open={dialogOpen}
          handleClose={() => setDialogOpen(false)}
          className={'w-full max-w-2xl max-h-full'}
        >
          <span className={cn('text-[11px] font-semibold uppercase tracking-widest mb-1', colourConfig.text)}>
            {activity.kit_name}
          </span>
          <DialogTitle asChild={true}>
            <h3 className={'text-2xl font-semibold text-graphite-500 mb-4'}>{activity.name}</h3>
          </DialogTitle>
          <ActivityMetaList activity={activity} iconColour={colourConfig.hex} />
          <div
            style={{ '--list-color': colourConfig.hex } as React.CSSProperties}
            className={'text-gray-800 overflow-y-auto'}
            dangerouslySetInnerHTML={descriptionHtml}
          />
        </GenericDialog>
      </div>
    </div>
  )
}

export { ActivityCardContent }
